import { useState, useCallback } from 'react';
import { notificationService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';
import { Table } from '../types';

const log = createLogger('seating');

interface UseNotificationOptions {
  inviteCode: string;
  userId?: string;
  tables: Table[];
  setTables: React.Dispatch<React.SetStateAction<Table[]>>;
  saveSeatingArrangement: (tables: Table[]) => void;
  editingDisabled: boolean;
}

export function useNotification({
  inviteCode,
  userId,
  tables,
  setTables,
  saveSeatingArrangement,
  editingDisabled,
}: UseNotificationOptions) {
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });

  const handleTableNotification = useCallback(() => {
    if (!userId) return;

    const assignedGuestsWithEmail = tables
      .flatMap(table => table.guests)
      .filter(guest => guest.email && !guest.notificationSent);

    if (assignedGuestsWithEmail.length === 0) {
      alert('所有賓客都已收到桌號通知，或者沒有賓客有 Email 地址！');
      return;
    }

    setShowNotificationModal(true);
  }, [userId, tables]);

  const handleSendNotifications = useCallback(async (selectedTables: number[] | 'all') => {
    if (!userId) return;

    try {
      setNotificationLoading(true);
      setShowNotificationModal(false);

      let targetGuests;

      if (selectedTables === 'all') {
        targetGuests = tables
          .flatMap(table => table.guests.map(guest => ({ ...guest, tableName: table.name })))
          .filter(guest => guest.email && !guest.notificationSent);
      } else {
        targetGuests = tables
          .filter(table => selectedTables.includes(table.id))
          .flatMap(table => table.guests.map(guest => ({ ...guest, tableName: table.name })))
          .filter(guest => guest.email && !guest.notificationSent);
      }

      if (targetGuests.length === 0) {
        alert('沒有可發送通知的賓客！');
        return;
      }

      log.debug('Table assignment summary:');
      const tableGroups = targetGuests.reduce((acc, guest) => {
        if (!acc[guest.tableName]) acc[guest.tableName] = [];
        acc[guest.tableName].push(guest.name);
        return acc;
      }, {} as Record<string, string[]>);

      Object.entries(tableGroups).forEach(([tableName, guestNames]) => {
        log.debug(`  ${tableName} (${guestNames.length} guests): ${guestNames.join(', ')}`);
      });

      log.debug(`Sending table notifications to ${targetGuests.length} guests...`);

      setSendingProgress({ current: 0, total: targetGuests.length });

      let successCount = 0;
      let failCount = 0;
      const notificationUpdates: Array<{ guestId: string; success: boolean }> = [];

      for (const [index, guest] of targetGuests.entries()) {
        try {
          setSendingProgress({ current: index + 1, total: targetGuests.length });

          await notificationService.sendTableNotification({
            guestName: guest.name,
            guestEmail: guest.email!,
            tableName: guest.tableName,
            inviteCode: inviteCode,
          });

          notificationUpdates.push({ guestId: guest.id, success: true });
          successCount++;
        } catch (error) {
          log.error(`Failed to notify ${guest.name}:`, error);
          notificationUpdates.push({ guestId: guest.id, success: false });
          failCount++;
        }
      }

      const finalUpdatedTables = tables.map(table => ({
        ...table,
        guests: table.guests.map(g => {
          const update = notificationUpdates.find(u => u.guestId === g.id);
          if (update) {
            return {
              ...g,
              notificationSent: update.success,
              notificationFailed: !update.success,
              notificationSentAt: update.success ? new Date() : g.notificationSentAt,
            };
          }
          return g;
        }),
      }));

      setTables(finalUpdatedTables);
      if (!editingDisabled) {
        saveSeatingArrangement(finalUpdatedTables);
      }

      setSendingProgress({ current: 0, total: 0 });

      log.info(`Sending complete: ${successCount} success, ${failCount} failed`);

      if (failCount === 0) {
        alert(`桌號通知發送完成！\n成功發送給 ${successCount} 位賓客。`);
      } else {
        alert(
          `桌號通知發送完成！\n成功: ${successCount} 位\n失敗: ${failCount} 位\n\n請檢查失敗的賓客並稍後重試。`
        );
      }
    } catch (error) {
      log.error('Batch notification sending failed:', error);
      alert('發送桌號通知時發生錯誤，請稍後再試。');
    } finally {
      setSendingProgress({ current: 0, total: 0 });
      setNotificationLoading(false);
    }
  }, [userId, tables, setTables, saveSeatingArrangement, inviteCode, editingDisabled]);

  return {
    notificationLoading,
    showNotificationModal,
    setShowNotificationModal,
    sendingProgress,
    handleTableNotification,
    handleSendNotifications,
  };
}
